import { Album as AlbumIcon, HardDrive, Music, Pause, Play } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useLocalLibraryStore } from "../../stores/localLibraryStore";
import { usePlayerStore } from "../../stores/playerStore";
import { Track } from "../../types";
import { formatFileSize } from "../../utils/formatFileSize";
import { formatTime } from "../../utils/formatTime";
import EqualizerPlayingAnimation from "../EqualizerPlayingAnimation";
import { TrackListHeader } from "./TrackListHeader";
import { TrackListInfo } from "./TrackListInfo";
import { TrackMenu } from "./TrackMenu";
import { SortDirection, SortKey, TrackListProps } from "./types";

export const TrackList: React.FC<TrackListProps> = ({
  tracks,
  onPlay,
  onRemove,
  showLocalControls = false,
  showAlbum = true,
  showHeader = true,
  title,
  subtitle,
  image,
  compact = false,
}) => {
  const { currentTrack, isPlaying, setCurrentTrack, setIsPlaying, setQueue } = usePlayerStore();
  const { addToRecentlyPlayed, updateTrackMetadata, getTrack } = useLocalLibraryStore();
  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredTrackId, setHoveredTrackId] = useState<string | null>(null);
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null);

  const showFileSize = tracks[0]?.source === "local";
  const hideColumn = compact && !showAlbum && !showFileSize && !showHeader;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const filteredAndSortedTracks = useMemo(() => {
    let result = [...tracks];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (track) =>
          track.title.toLowerCase().includes(query) ||
          track.artist.toLowerCase().includes(query) ||
          track.album?.toLowerCase().includes(query)
      );
    }

    return result.sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case "title":
          comparison = a.title.localeCompare(b.title);
          break;
        case "artist":
          comparison = a.artist.localeCompare(b.artist);
          break;
        case "album":
          comparison = (a.album || "Unknown Album").localeCompare(b.album || "Unknown Album");
          break;
        case "duration":
          comparison = a.duration - b.duration;
          break;
        case "size":
          comparison = (a.fileSize || 0) - (b.fileSize || 0);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [tracks, sortKey, sortDirection, searchQuery]);

  const handlePlay = async (track: Track) => {
    try {
      setLoadingTrackId(track.id);
      console.log("=== TrackList: Play clicked for track:", track.title);

      const isSameTrack = currentTrack?.id === track.id;

      if (isSameTrack) {
        // Same track - just toggle playback state
        console.log("=== TrackList: Same track, toggling playback state");
        setIsPlaying(!isPlaying);
        return;
      }

      // Different track - set up new track
      console.log("=== TrackList: Different track, setting up new track");
      let updatedTrack = track;

      if (track.source === "local") {
        const fetchedTrack = await getTrack(track.id);
        if (fetchedTrack) {
          updatedTrack = fetchedTrack;
          console.log("=== TrackList: Fetched updated track data");
        }
      }

      // Set new track and queue
      setCurrentTrack(updatedTrack);
      setQueue(filteredAndSortedTracks);
      addToRecentlyPlayed(updatedTrack);

      // Wait for track to be set up, then start playback
      setTimeout(() => {
        console.log("=== TrackList: Starting playback after track setup delay");
        setIsPlaying(true);
      }, 150); // Give useAudioPlayer time to set up the track

      onPlay?.(updatedTrack);
    } catch (error) {
      console.error("=== TrackList: Failed to play track:", error);
    } finally {
      setLoadingTrackId(null);
    }
  };

  const handlePause = () => {
    console.log("=== TrackList: Pause clicked");
    setIsPlaying(false);
  };

  const handleSave = (trackId: string, updates: Partial<Track>) => {
    updateTrackMetadata(trackId, updates);
  };

  const UnknownLabel = ({ icon: Icon, label }: { icon: any; label: string }) => (
    <div className="flex items-center gap-2 text-zinc-400">
      {false && <Icon className="w-4 h-4 flex-shrink-0" />}
      <span className="truncate">{label}</span>
    </div>
  );

  const renderTrackIndicator = (track: Track, index: number) => {
    const isCurrentTrack = currentTrack?.id === track.id;
    const isHovered = hoveredTrackId === track.id;
    const isLoading = loadingTrackId === track.id;

    if (isLoading) {
      return (
        <div className="animate-pulse">
          <div className="w-4 h-4 bg-zinc-600 rounded-full"></div>
        </div>
      );
    }

    if (isCurrentTrack) {
      if (isPlaying) {
        return isHovered ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePause();
            }}
            className="flex items-center justify-center"
          >
            <Pause className="w-4 h-4 fill-yellow-500 text-yellow-500" />
          </button>
        ) : (
          <EqualizerPlayingAnimation
            height={18}
            barWidth={2}
            barCount={3}
            animated={true}
            className="rounded-lg p-0.5 overflow-hidden"
          />
        );
      } else {
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePlay(track);
            }}
          >
            <Play className="w-4 h-4 fill-yellow-500 text-yellow-500" />
          </button>
        );
      }
    }

    return (
      <button
        className="invisible group-hover:visible"
        onClick={(e) => {
          e.stopPropagation();
          handlePlay(track);
        }}
      >
        <Play className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div className="w-full">
      {showHeader && (
        <TrackListInfo
          tracks={tracks}
          title={title}
          subtitle={subtitle}
          image={image}
          showHeader={showHeader}
          onSearch={setSearchQuery}
        />
      )}

      <TrackListHeader
        showAlbum={showAlbum}
        showFileSize={showFileSize}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        hideColumn={!compact}
      />

      {filteredAndSortedTracks.map((track, index) => (
        <div
          key={track.id}
          className={`flex items-center px-4 py-2 hover:bg-zinc-800 group cursor-pointer ${
            currentTrack?.id === track.id ? "bg-zinc-800" : ""
          }`}
          onClick={() => handlePlay(track)}
          onMouseEnter={() => setHoveredTrackId(track.id)}
          onMouseLeave={() => setHoveredTrackId(null)}
        >
          <div className="w-[24px] mr-[7px] flex items-center justify-center">
            {renderTrackIndicator(track, index)}
            <span
              className={`${
                currentTrack?.id === track.id || isPlaying ? "invisible" : ""
              } visible group-hover:invisible -translate-x-3.5`}
            >
              {index + 1}
            </span>
          </div>

          {/* Mobile Layout */}
          <div className="flex-1 flex items-center gap-3 min-w-0 lg:hidden">
            {track.albumArt ? (
              <img
                src={track.albumArt}
                alt=""
                className="w-10 h-10 rounded bg-zinc-800 flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling?.classList.remove("hidden");
                }}
              />
            ) : null}
            <div
              className={`w-10 h-10 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0 ${
                track.albumArt ? "hidden" : ""
              }`}
            ></div>
            <div className="min-w-0 flex-1">
              <a
                href={`/tracks/${track.id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium truncate hover:text-yellow-500 transition block"
              >
                {track.title || "Unknown Title"}
              </a>
              <div className="text-sm text-zinc-400 truncate">{track.artist || "Unknown Artist"}</div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex flex-1 items-center gap-3 min-w-[200px]">
            {track.albumArt ? (
              <img
                src={track.albumArt}
                alt=""
                className="w-10 h-10 rounded bg-zinc-800 flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling?.classList.remove("hidden");
                }}
              />
            ) : null}
            <div
              className={`w-10 h-10 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0 ${
                track.albumArt ? "hidden" : ""
              }`}
            >
              <Music className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="min-w-0 flex-1">
              <a
                href={`/tracks/${track.id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium truncate hover:text-yellow-500 transition inline-block w-11/12"
              >
                {track.title || "Unknown Title"}
              </a>
            </div>
          </div>

          <div className="w-[200px] hidden lg:flex items-center gap-2">
            <span className="truncate inline-block w-11/12 text-zinc-400">{track.artist || "Unknown Artist"}</span>
          </div>

          {showAlbum && !compact && (
            <div className="w-[200px] hidden lg:flex min-w-0">
              {track.album ? (
                <a
                  href={`/albums/${track.albumId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:text-yellow-500 transition truncate inline-block w-11/12 items-center gap-2"
                >
                  {track.album}
                </a>
              ) : (
                <UnknownLabel icon={AlbumIcon} label="Unknown Album" />
              )}
            </div>
          )}

          {track.source === "local" && !compact && (
            <div className="w-[100px] text-zinc-400 hidden lg:block text-left">
              {track.fileSize ? formatFileSize(track.fileSize) : <UnknownLabel icon={HardDrive} label="Unknown Size" />}
            </div>
          )}

          <div className="w-[100px] flex items-center justify-end gap-4">
            <span className="text-sm text-zinc-400">{formatTime(track.duration)}</span>
            <TrackMenu
              track={track}
              onDelete={onRemove ? () => onRemove(track.id) : undefined}
              onSave={(updates) => handleSave(track.id, updates)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
